import { HttpInterceptorFn } from '@angular/common/http';
import { HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { HeaderService } from './header.service';
import { AuthService } from '../../auth/services/auth.service';
import { RefreshTokenRes } from '../../auth/models/auth.model';
import { ApiResponse } from '../models/common.model';

let isRefreshingToken = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

/**
 * Function-based HTTP interceptor for handling authentication and token refresh.
 */
export const tokenInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {

  // Inject dependencies manually (since we don't have a constructor)
  const headerService = inject(HeaderService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Set headers before sending the request
  request = setHeaders(request, headerService);

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        return handleError(request, next, authService, router, headerService);
      } else if (![406, 422, 408, 400, 403].includes(err.status)) {
        decideErrorMessage(err.statusText);
      }
      return throwError(() => err);
    })
  );
};

/**
 * Handles 401 Unauthorized errors by attempting token refresh.
 */
function handleError(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  headerService: HeaderService
): Observable<HttpEvent<any>> {
  if (!isRefreshingToken) {
    isRefreshingToken = true;
    refreshTokenSubject.next(null);
    return authService.getRefreshToken().pipe(
      switchMap((res: ApiResponse<RefreshTokenRes>) => {
        isRefreshingToken = false;
        refreshTokenSubject.next(res.data.accessToken);
        const currentUser = JSON.parse(sessionStorage.getItem('currentUserToken') || '{}');
        sessionStorage.setItem('currentUserToken', JSON.stringify({
          token: res.data.accessToken,
          refreshToken: currentUser.refreshToken
        }));
        return next(setHeaders(request, headerService));
      }),
      catchError((err: HttpErrorResponse) => {
        isRefreshingToken = false;
        router.navigate(['/signin']);
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => next(setHeaders(request, headerService)))
    );
  }
}

/**
 * Sets custom headers for outgoing HTTP requests.
 */
function setHeaders(request: HttpRequest<any>, headerService: HeaderService): HttpRequest<any> {
  const customHeaders = { App: 'Zen Campaigns' };
  let headers = headerService.getHeaders(request.url);
  headers = { ...customHeaders, ...headers };
  return headers ? request.clone({ setHeaders: headers }) : request;
}

/**
 * Displays error messages based on status text.
 */
function decideErrorMessage(statusText: string) {
  let errorMessage: string;
  switch (statusText) {
    case 'Unknown Error':
      errorMessage = 'Something went wrong. Please check your network connection.';
      break;
    case 'Unauthorized':
      errorMessage = 'Unauthorized to access the service. Please sign in again.';
      break;
    case 'Not Found':
      errorMessage = 'No service is provided by the server';
      break;
    case 'Unprocessable Entity':
      errorMessage = 'Please check your network connection';
      break;
    case 'Internal Server Error':
      errorMessage = 'Something has gone wrong with the website';
      break;
    default:
      errorMessage = 'An error occurred';
  }
  console.log('errorMessage');
}
